import { useApolloClient, useMutation } from "@apollo/client";
import { useContext, useRef, useState } from "react";
import {
  CreateAssessmentAnswerCalibrationsMutation,
  CreateAssessmentAnswerCalibrationsMutationVariables,
} from "types/graphql-schema";

import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import createAssessmentAnswerCalibrationsMutation from "@apps/reporting/graphql/create-assessment-answer-calibrations-mutation";
import getAssessmentAnswerCalibrationsQuery from "@apps/reporting/graphql/get-assessment-answer-calibrations-query";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Modal from "@components/modal/modal";
import { ModalWidth } from "@components/modal/modal-constants";
import ModalTitle from "@components/modal/modal-title";
import TextareaAutosize from "@components/textarea-autosize/textarea-autosize";
import { assertNonNull } from "@helpers/helpers";
import { plainTextToTiptap } from "@helpers/tiptap-helpers";

import { NineBoxPerson, NineBoxQuestion } from "./nine-box/types";

// One axis of a move: the person's rating on that question, before and after.
export type NineBoxMoveChange = {
  question: NineBoxQuestion;
  answerId: number;
  from: number;
  to: number;
};

export type NineBoxMove = {
  person: NineBoxPerson;
  // The person, as the calibration's target.
  targetId: number;
  // The horizontal axis first, then the vertical one, if any.
  changes: NineBoxMoveChange[];
};

const ratingLabel = (question: NineBoxQuestion, value: number) =>
  question.labels[value - question.startValue] || `${value}`;

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Please try again.";

/**
 * Asks for the reason of a move on the calibration grid, then saves each
 * changed rating as a calibration with that reason as its comment: the same
 * checks as the review's side panel. A move across both axes is one save:
 * both ratings are saved, or neither is. The server checks that the viewer
 * may change the ratings.
 */
const NineBoxMovePersonDialog = ({
  move,
  onClose,
}: {
  move: NineBoxMove;
  onClose: () => void;
}) => {
  const { complianceProgramId, complianceProgram } = useContext(
    PerformanceAssessmentReportContext,
  );
  const client = useApolloClient();
  const [reason, setReason] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const [createAssessmentAnswerCalibrations] = useMutation<
    CreateAssessmentAnswerCalibrationsMutation,
    CreateAssessmentAnswerCalibrationsMutationVariables
  >(createAssessmentAnswerCalibrationsMutation);

  const { person, changes } = move;
  const changed = changes.filter(({ from, to }) => from !== to);
  const canMove = reason.trim() !== "" && !isSaving;

  const handleMove = async () => {
    if (!canMove) {
      return;
    }
    setIsSaving(true);
    // One paragraph per line; an empty line is an empty paragraph.
    const comment = JSON.stringify(plainTextToTiptap(reason.trim()));
    try {
      await createAssessmentAnswerCalibrations({
        variables: {
          complianceProgramId,
          templateId: assertNonNull(
            complianceProgram?.performanceAssessmentTemplate,
          ).id,
          targetId: move.targetId,
          comment,
          isSuggestion: false,
          isPrecalibration: false,
          ratings: changed.map((change) => ({
            questionId: change.question.id,
            answerId: change.answerId,
            integerAnswer: change.to,
          })),
        },
        // The error below names the person: no generic toast.
        context: { skipGraphqlErrorHandling: true },
      });
    } catch (error) {
      // Nothing is saved: the dialog stays open, with the reason.
      setIsSaving(false);
      errorNotificationVar({
        title: `${person.name} was not moved`,
        description: errorMessage(error),
      });
      return;
    }
    // The side panel's calibration history.
    client
      .refetchQueries({ include: [getAssessmentAnswerCalibrationsQuery] })
      .catch(() => {});
    successNotificationVar({
      title: `${person.name} moved to ${changes
        .map(({ question, to }) => ratingLabel(question, to))
        .join(" · ")}`,
    });
    onClose();
  };

  return (
    <Modal
      open
      onClose={isSaving ? undefined : onClose}
      width={ModalWidth.small}
      initialFocus={reasonRef}
      alignedTop
      aria-label="Move person dialog"
    >
      <form
        className="flex flex-col gap-4 p-6"
        onSubmit={(event) => {
          event.preventDefault();
          handleMove();
        }}
      >
        <ModalTitle>{`Move ${person.name}?`}</ModalTitle>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          {changes.map(({ question, from, to }) => (
            <div key={question.id} className="contents">
              <dt className="text-gray-500">{question.title}</dt>
              <dd>
                {from === to ? (
                  `${ratingLabel(question, to)} (no change)`
                ) : (
                  <>
                    {`${ratingLabel(question, from)} → `}
                    <span className="font-semibold text-violet-700">
                      {ratingLabel(question, to)}
                    </span>
                  </>
                )}
              </dd>
            </div>
          ))}
        </dl>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="nine-box-move-reason"
            className="text-sm font-medium text-gray-700"
          >
            Why? (required)
          </label>
          <TextareaAutosize
            id="nine-box-move-reason"
            ref={reasonRef}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="For example: led the billing migration alone this half."
            value={reason}
            disabled={isSaving}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="text-xs text-gray-500">
            Saved as the calibration comment on each changed rating.
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button text="Cancel" disabled={isSaving} onClick={onClose} />
          <Button
            type="submit"
            theme={buttonTheme.primary}
            text={isSaving ? "Moving..." : "Move"}
            disabled={!canMove}
          />
        </div>
      </form>
    </Modal>
  );
};

export default NineBoxMovePersonDialog;
